import { useEffect } from 'react';
import { MapPin, Clock, Users, Heart, Briefcase, GraduationCap } from 'lucide-react';

const Careers = () => {
  useEffect(() => {
    document.title = 'Careers - FemSportNet';
  }, []);

  const benefits = [
    {
      icon: <Heart className="h-6 w-6 text-green-500" />,
      title: 'Health & Wellness',
      description: 'Comprehensive health insurance, mental health support, and wellness programs'
    },
    {
      icon: <Clock className="h-6 w-6 text-green-500" />,
      title: 'Work-Life Balance',
      description: 'Flexible working hours, remote work options, and unlimited PTO'
    },
    {
      icon: <GraduationCap className="h-6 w-6 text-green-500" />,
      title: 'Learning & Development',
      description: 'Professional development budget, conference attendance, and skill training'
    },
    {
      icon: <Users className="h-6 w-6 text-green-500" />,
      title: 'Inclusive Culture',
      description: 'Diverse team, equal opportunities, and supportive work environment'
    }
  ];

  const openings = [
    {
      title: 'Senior Frontend Developer',
      department: 'Engineering',
      location: 'Remote / San Francisco',
      type: 'Full-time',
      description: 'Join our engineering team to build beautiful, responsive user interfaces that empower female athletes worldwide.',
      requirements: [
        '5+ years of React/TypeScript experience',
        'Strong CSS and responsive design skills',
        'Experience with modern frontend tooling',
        'Passion for creating inclusive products'
      ]
    },
    {
      title: 'Community Manager',
      department: 'Community',
      location: 'Remote / New York',
      type: 'Full-time',
      description: 'Help build and nurture our growing community of female athletes, coaches, and sports professionals.',
      requirements: [
        'Experience in community management',
        'Strong communication skills',
        'Background in sports or athletics preferred',
        'Social media and content creation experience'
      ]
    },
    {
      title: 'Product Designer',
      department: 'Design',
      location: 'Remote / Los Angeles',
      type: 'Full-time',
      description: 'Design intuitive and accessible experiences that help athletes connect, grow, and succeed.',
      requirements: [
        'Strong portfolio in product design',
        'Experience with design systems',
        'User research and testing experience',
        'Figma and prototyping skills'
      ]
    },
    {
      title: 'Sports Partnerships Manager',
      department: 'Business Development',
      location: 'Remote / Chicago',
      type: 'Full-time',
      description: 'Build strategic partnerships with sports organizations, teams, and brands to create opportunities for our athletes.',
      requirements: [
        'Experience in sports industry partnerships',
        'Strong negotiation and relationship skills',
        'Network in women\'s sports preferred',
        'Business development experience'
      ]
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Join Our Team
            </h1>
            <p className="text-xl text-green-100 mb-8">
              Help us build the future of women's sports. Join a passionate team dedicated to 
              empowering female athletes worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Our Culture
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                We believe in creating an environment where everyone can thrive and make a meaningful impact.
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 mb-12">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                What Makes Us Different
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Mission-Driven</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Every team member is passionate about supporting female athletes and creating positive change in sports.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Innovation-Focused</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    We encourage creative thinking and provide the resources to turn great ideas into reality.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Collaborative</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    We work together across teams and departments to achieve our shared goals.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Growth-Oriented</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    We invest in our team's professional development and provide opportunities for advancement.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Benefits & Perks
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                We take care of our team so they can focus on what they do best
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white dark:bg-gray-700 rounded-lg p-6 text-center">
                  <div className="inline-flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900 p-3 mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Open Positions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Find your next opportunity to make a difference
              </p>
            </div>
            
            <div className="space-y-6">
              {openings.map((job, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-300">
                        <div className="flex items-center">
                          <Briefcase size={16} className="mr-1" />
                          {job.department}
                        </div>
                        <div className="flex items-center">
                          <MapPin size={16} className="mr-1" />
                          {job.location}
                        </div>
                        <div className="flex items-center">
                          <Clock size={16} className="mr-1" />
                          {job.type}
                        </div>
                      </div>
                    </div>
                    <button className="mt-4 md:mt-0 bg-green-500 hover:bg-green-600 text-white font-medium px-6 py-2 rounded-md transition-colors duration-200">
                      Apply Now
                    </button>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    {job.description}
                  </p>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Requirements:</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                      {job.requirements.map((req, reqIndex) => (
                        <li key={reqIndex}>{req}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Don't See the Right Role?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            We're always looking for talented individuals who share our passion for empowering female athletes.
          </p>
          <button className="bg-white text-green-600 font-medium px-8 py-3 rounded-md hover:bg-green-50 transition-colors duration-200">
            Send Us Your Resume
          </button>
        </div>
      </section>
    </div>
  );
};

export default Careers;